import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Chip, Container, Dialog, DialogTitle, Grid, Typography } from "@mui/material"
import { pdf } from "@react-pdf/renderer"
import { hasRole } from "auth/hasRole"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import EditFormTemplate from "components/Form/FormTemplate/EditFormTemplate"
import { PdfFormTemplate } from "components/Form/FormTemplate/PdfFormTemplate"
import ViewFormTemplate from "components/Form/FormTemplate/ViewFormTemplate"
import LoadingPage from "components/Loading/LoadingPage"
import PaperBox from "components/PaperBox"
import { apiEndpoints } from "generated/apiEndpoints"
import EditDeviationForm from "pages/deviation/pages/components/EditDeviationForm"
import { deviationPriorityProps } from "pages/deviation/pages/helpers/deviationPriority"
import { deviationStatusProps } from "pages/deviation/pages/helpers/deviationStatus"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { IFormAreaProps, InsuranceCompany } from "components/Form/FormArea/formarea.interface"
import DropdownMenu from "components/DropdownMenu/DropdownMenu"
import { deviationEmailValues } from "pages/deviation/pages/helpers/deviationEmailValues"
import SuccessDialog from "components/Dialogs/SuccessDialog"
import DeviationInsuranceCompanyForm from "pages/deviation/pages/components/DeviationInsuranceCompanyForm"
import { IFormGroupProps } from "components/Form/FormGroup/formgroup.interface"
import { IFormFieldProps } from "components/Form/FormField/formfield.interface"
import { useApiGet } from "auth/useApi2"
import { FormTemplateKey } from "helpers/FormTemplate/FormTemplateKey"

const DeviationDetails: React.FC = ({ match }: any) => {
  const appContext = useContext(AppContext)
  const [deviation, getDeviation] = useApi() as [IApiResult, IApiFunc]
  const [formResult, getForm] = useApi() as [IApiResult, IApiFunc]
  const [attachmentResult, attachmentApi] = useApi() as [IApiResult, IApiFunc]
  const [editForm, setEditForm] = useState<boolean>(false)
  const [formData, setFormData] = useState<any>(null)
  const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false)
  const [showSendMailResultDialog, setShowSendMailResultDialog] = useState<boolean>(false)
  const [sendMailResult, sendMailApi] = useApi() as [IApiResult, IApiFunc]
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [sentEmailRecipients, setSentEmailRecipients] = useState<string[]>([])
  const [customerResult, getCustomer] = useApi() as [IApiResult, IApiFunc]
  const { t } = useTranslation()
  const [defaultSubject, setDefaultSubject] = useState<Record<string, string>>({
    deviation: "",
    regNr: ""
  })
  const [postFormResult, postForm] = useApi() as [IApiResult, IApiFunc]
  const [insuranceCompanyEmails, setInsuranceCompanyEmails] = useState<InsuranceCompany[]>([])
  const [insuranceCompletedField, setInsuranceCompletedField] = useState<string | null>(null);
  const [insuranceCompletedFieldId, setInsuranceCompletedFieldId] = useState<string | null>(null);
  const [initialFieldValues, setInitialFieldValues] = useState<any>({})
  const [logoResult, getLogoData] = useApiGet() as any

  useEffect(() => {
    getDeviation.get(apiEndpoints.deviation.getbyid.replace("{id}", match.params.id))
  }, [getDeviation, match])

  useEffect(() => {
    if (deviation.status === 2 && !editForm) {
      getForm.get(
        apiEndpoints.form.getformresultbyid
          .replace("{id}", deviation.data?.formResult?.id)
          .replace("{formTemplate}", deviation.data?.formResult?.formTemplate?.childFeatures[0])
      )
    }
  }, [getForm, deviation, editForm])

  useEffect(() => {
    if (attachmentResult.status !== 2) return

    setFormData((prevFormData: any) => {
      const clonedFormData = structuredClone(prevFormData)

      clonedFormData.formAreas.forEach((area: IFormAreaProps) => {
        area.formGroups.forEach(group => {
          group.formFields.forEach(field => {
            if (field.component === "Upload") {
              const files = typeof field.value === "string" ? JSON.parse(field.value) : field.value
              field.value = files.map((fileInfo: any) => {
                if (fileInfo?.fileId === attachmentResult?.data?.fileid) {
                  return { ...fileInfo, Data: attachmentResult.data.data }
                }
                return fileInfo
              })
            }
          })
        })
      })

      return clonedFormData
    })
  }, [attachmentResult])

  useEffect(() => {
    if (formResult.status !== 2) return
    formResult?.data?.formAreas.forEach((area: IFormAreaProps) => {
      area.formGroups.forEach(group => {
        group.formFields.forEach(field => {
          if (field.component === "Upload") {
            const isDataMissing = field?.value === null || field?.value === undefined
            if (isDataMissing) return
            const files = typeof field.value === "string" ? JSON.parse(field.value) : field.value
            files.forEach((fileInfo: any) => {
              const fileId = fileInfo.fileId.replace("deviation/", "")
              let url = apiEndpoints.attachment.getattachments.replace("{fileid}", fileId)
              url = url.replace("{feature}", "deviation")
              attachmentApi.get(url)
            })
          }
        })
      })
    })
    setFormData(formResult.data)
  }, [formResult])

  useEffect(() => {
    const areas = formData?.formAreas || [];
    const adminArea = areas.find(
      (area: IFormAreaProps) =>
        area.stepLabel === "Adminfrågor" || area.stepLabel === "Admin questions"
    );

    if (!adminArea) return;

    const fieldValue = adminArea.formGroups
      ?.flatMap((group: any) => group.formFields)
      .find(
        (field: any) =>
          field.labelDefault === "Kompletterad till försäkringsbolag" ||
          field.labelDefault === "Completed for insurance companies"
      );

    if (fieldValue) {
      setInsuranceCompletedFieldId(fieldValue?.id);
      setInsuranceCompletedField(fieldValue?.value);
    }
  }, [formData]);

  const isDeviationAdmin = hasRole(appContext.appUser, formData?.childFeatures[0].toString() + "Admin")
  const deviationPriorityData = deviationPriorityProps(deviation.data?.priority)
  const deviationStatusData = deviationStatusProps(deviation.data?.status)
  const subjectString = defaultSubject && Object.values(defaultSubject).filter(Boolean).join(" ")
  const formTemplateKey = formResult.data?.key

  const emailObject = useMemo(() => {
    const obj: Record<string, any> = {}
    if (!formData) return obj
    const excludedFormFields = [
      "action",
      "comment",
      "describe_event",
      "could_do_differently",
      "what_consequences_operation",
      "how_to_avoid_event",
      "tips_act_situation",
      "action_education_request",
    ]

    formData.formAreas?.forEach((area: any) => {
      area.formGroups?.forEach((group: any) => {
        group.formFields?.forEach((field: any, fieldIndex: number) => {
          if (excludedFormFields.includes(field.labelKey)) return

          const nextField = group.formFields[fieldIndex]
          const showDriverBlock = nextField?.labelKey === "vehicle_damage_S4_FG1_FF6" && formTemplateKey === FormTemplateKey.VehicleDamage

          if (showDriverBlock && deviation.data?.involvedUserName) {
            obj[t("global.driver", "Driver")] = deviation.data?.involvedUserName
          }

          if (field.component === "Upload") {
            setUploadedFiles(field.value)
          } else if (
            field.component === "VehiclePicker"
          ) {
            setDefaultSubject(prev => ({ ...prev, regNr: JSON.parse(field.value)?.RegNumber ?? "" }))
          }
          const result = deviationEmailValues(field, t)
          if (!result) return
          const { label, value } = result
          obj[label] = value
        })
      })
    })

    return obj
  }, [formData, t, deviation.data])

  const emailBodyFromObject = useMemo(() => {
    if (!emailObject || Object.keys(emailObject).length === 0) return ""
    return Object.entries(emailObject)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n")
  }, [emailObject])

  const htmlBodyFromObject = useMemo(() => {
    if (!emailObject || Object.keys(emailObject).length === 0) return ""

    const rows = Object.entries(emailObject)
      .map(([key, value]) => `<p><b>${key}:</b> ${value ?? "-"}</p>`)
      .join("")

    return `
    <html>
      <body style="font-family: Arial, sans-serif; font-size: 14px; color: #000;">
        ${rows}
      </body>
    </html>
  `
  }, [emailObject])

  useEffect(() => {
    formData?.formAreas &&
      formData?.formAreas.length > 0 &&
      formData?.formAreas.map((area: IFormAreaProps) => {
        return area.formGroups.map((formGroup: IFormGroupProps) => {
          return formGroup.formFields.map((field: IFormFieldProps) => {
            const newObj = {
              [field.id]: field.value
            }
            return setInitialFieldValues((prev: any) => ({ ...prev, ...Object.assign(newObj) }))

          })

        })
      })
  }, [formData?.formAreas])

  const safeString = (val: any) => {
    if (val === undefined || val === null) return undefined;
    if (typeof val === "object") {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  useEffect(() => {
    if (sendMailResult.status === 2) {
      setShowEmailDialog(false);
      setShowSendMailResultDialog(true);

      const mapValues: Array<any> = initialFieldValues
        ? Object.entries(initialFieldValues).map(([key, value]) => ({
          formResultId: deviation.data?.formResult?.id,
          formFieldId: key,
          value: safeString(value),
        }))
        : [];

      const targetFieldId = insuranceCompletedFieldId?.toString();
      const newValue = "true";
      const existingIndex = mapValues.findIndex(
        (a) => a.formFieldId === targetFieldId
      );

      if (existingIndex > -1) {
        mapValues[existingIndex].value = newValue;
      } else {
        mapValues.push({
          formResultId: deviation.data?.formResult?.id,
          formFieldId: targetFieldId,
          value: newValue,
        });
      }

      const postValues = {
        id: deviation.data?.formResult?.id,
        formTemplateId: formData?.id,
        formAnswers: mapValues,
      };
      postForm.post(apiEndpoints.form.createorupdateformresult, postValues);
    }
  }, [sendMailResult]);


  useEffect(() => {
    if (postFormResult.status === 2) {
      getDeviation.get(apiEndpoints.deviation.getbyid.replace("{id}", match.params.id))
    }
  }, [postFormResult])

  useEffect(() => {
    getCustomer.get(apiEndpoints.customeradmin.getcustomer)
  }, [])

  useEffect(() => {
    if (customerResult.status === 2) {
      const insuranceCompanies = customerResult.data?.companies
        ?.filter((company: any) => company.insuranceCompanyEmail)
        ?.map((company: any) => ({
          name: company.name,
          insuranceCompanyEmail: company.insuranceCompanyEmail,
        }))

      setInsuranceCompanyEmails(insuranceCompanies || [])
    }
  }, [customerResult])

  useEffect(() => {
    if (formResult.status === 2) {
      const findKey = t(`deviation.form_key.${formResult.data?.key}`, "Deviation")
      setDefaultSubject(prev => ({
        ...prev,
        deviation: findKey || ""
      }))
    }
  }, [formResult])

  useEffect(() => {
    if (customerResult.data && customerResult.data.logoName) {
      getLogoData(
        apiEndpoints.customeradmin.getcustomerlogobyname.concat("?name=" + customerResult.data.logoName)
      )
    }
  }, [customerResult, getLogoData])

  return (
    <Container>
      <Grid container spacing={3} display={"flex"} justifyContent={"space-between"}>
        <Grid item md={9} xs={12}>
          <PaperBox sx={{ m: 0 }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={2} marginBottom={2}>
              <Grid item>
                {insuranceCompletedField !== null ? (
                  <Chip
                    color={
                      insuranceCompletedField === "true"
                        ? "success"
                        : "default"
                    }
                    label={t(
                      "deviation.vehicle_damage_S4_FG1_FF5",
                      "Completed for insurance companies"
                    )}
                  />
                ) : null}

              </Grid>
              <Grid item>
                {!editForm && <DropdownMenu
                  t={t}
                  buttonLabel={t("deviation.manage_deviation", "Manage deviation")}
                  endIcon={faChevronDown}
                  items={[
                    isDeviationAdmin && {
                      label: editForm ? t("global.cancel", "Cancel") : t("deviation.edit_deviation", "Edit deviation"),
                      onClick: () => setEditForm(!editForm)
                    },
                    formData !== null && {
                      label: t("global.download_as_pdf", "Download as PDF"),
                      onClick: async () => {
                        let blobPDF = await pdf(
                          <PdfFormTemplate
                            {...formData}
                            deviationResult={deviation}
                            logoResult={(logoResult as any).status === 2 && (logoResult as any).data}
                            allDeviationData={formResult.status === 2 && formResult.data}
                          />).toBlob()
                        const link = document.createElement("a")
                        link.href = window.URL.createObjectURL(blobPDF)
                        link.download = `${deviation?.data?.name}`
                        link.click()
                        window.URL.revokeObjectURL(link.href)
                      }
                    },
                    isDeviationAdmin && insuranceCompletedField !== null && insuranceCompletedField === "false" && {
                      label: t("global.send_to_insurance_company", "Send to insurance company"),
                      onClick: () => setShowEmailDialog(true)
                    },
                  ].filter(Boolean) as any}
                />}
                {isDeviationAdmin && editForm && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() => setEditForm(!editForm)}>
                      {t("global.cancel", "Cancel")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {(deviation.status === 1 || formResult.status === 1) && <LoadingPage />}

            {formResult.status === 2 && (
              <>
                {deviation.status === 2 && formData && (
                  <>
                    {isDeviationAdmin &&
                      editForm ? (
                      <EditFormTemplate
                        insuranceCompletedFieldId={insuranceCompletedFieldId}
                        {...formData}
                        id={deviation.data?.formResult?.id}
                        formTemplateId={formData?.id}
                        feature={"Deviation"}
                        setEditForm={setEditForm}
                        translationData={{ formTypeKey: "deviation", formNameKey: formData?.key }}
                        createdByName={deviation.data.createdByName}
                      />
                    ) : (
                      <ViewFormTemplate
                        insuranceCompletedFieldId={insuranceCompletedFieldId}
                        {...formData}
                        deviation={deviation}
                        translationData={{ formTypeKey: "deviation", formNameKey: formData?.key }}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </PaperBox>
        </Grid>
        <Grid item md={3} xs={12}>
          {deviation.status === 2 && formResult.status === 2 && (isDeviationAdmin ? (
            <EditDeviationForm {...deviation.data} feature={formResult.data.childFeatures} />
          ) : (
            <>
              <Box marginBottom={2}>
                <Typography variant="inputLabel" fontWeight={500} color="textSecondary">
                  {t("global.status", "Status")}
                </Typography>

                <Box>
                  <FontAwesomeIcon
                    color={deviationStatusData.color}
                    icon={deviationStatusData.icon as IconProp}
                  />
                  <Typography
                    variant="labelMedium"
                    marginLeft={1}
                    color={deviationStatusData.color}>
                    {t(deviationStatusData.key, deviationStatusData.title)}
                  </Typography>
                </Box>
              </Box>
              <Box marginBottom={2}>
                <>
                  <Typography variant="inputLabel" fontWeight={500} color="textSecondary">
                    {t("deviation.priority", "Priority")}
                  </Typography>

                  <Chip
                    color={
                      deviationPriorityData?.color !== null
                        ? deviationPriorityData?.color
                        : "default"
                    }
                    variant="outlined"
                    label={t("global." + deviationPriorityData.key, deviationPriorityData?.title)}
                  />
                </>
              </Box>
              <Box marginBottom={2}>
                <>
                  <Typography variant="inputLabel" fontWeight={500} color="textSecondary">
                    {t("global.assigned", "Assigned")}
                  </Typography>
                  <Typography variant="bodyMedium" fontWeight={500}>
                    {deviation.data.assignedUser || "-"}
                  </Typography>
                </>
              </Box>
              <Box marginBottom={2}>
                <Typography variant="inputLabel" fontWeight={500} color="textSecondary">
                  {t("deviation.actions", "Actions")}
                </Typography>
                <Typography variant="bodyMedium" fontWeight={500}>
                  {deviation.data.actions || "-"}
                </Typography>
              </Box>
            </>
          ))}
        </Grid>
      </Grid>
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 4,
          },
        }}
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        fullWidth
      >
        <DialogTitle variant="h2" sx={{ px: 3, pt: 3, pb: 0 }}>
          {t("global.preview", "Preview")}
        </DialogTitle>

        <DeviationInsuranceCompanyForm
          subjectString={subjectString}
          emailBodyFromObject={emailBodyFromObject}
          htmlBodyFromObject={htmlBodyFromObject}
          uploadedFiles={uploadedFiles}
          insuranceCompanyEmails={insuranceCompanyEmails}
          emailObject={emailObject}
          setShowEmailDialog={setShowEmailDialog}
          setShowSendMailResultDialog={setShowSendMailResultDialog}
          sendMailApi={sendMailApi}
          sendMailResult={sendMailResult}
          t={t}
          setSentEmailRecipients={setSentEmailRecipients}
        />
      </Dialog>
      {
        showSendMailResultDialog &&
        <SuccessDialog
          resetModalState={setShowSendMailResultDialog}
          title={t(
            "global.email_sent",
            "Email sent"
          ) + "!"}
          message={
            <>
              {t("deviation.email_is_sent_to", "The email has been sent to:")}
              {sentEmailRecipients.map((email, i) => (
                <Box mt={2} key={i}>
                  <strong style={{ lineBreak: "anywhere" }}>{email}</strong>
                </Box>
              ))}
            </>
          }

        />
      }
    </Container >
  )
}

export default DeviationDetails
